// @flow
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container } from 'react-bootstrap';
import TempPasswordModal from '../components/TempPasswordModal/TempPasswordModal';

// actions
import { changeSidebarType } from '../store/ducks/layout';

// constants
import * as layoutConstants from '../constants/layout';

// utils
import { changeBodyAttribute } from '../utils';
import Notification from '../components/Notification';
import { listenerPartnerFirebase } from '../firebase';

// code splitting and lazy loading
// https://blog.logrocket.com/lazy-loading-components-in-react-16-6-6cea535c0b52
const Topbar = React.lazy(() => import('./Topbar'));
const LeftSidebar = React.lazy(() => import('./LeftSidebar'));
const Footer = React.lazy(() => import('./Footer'));

const loading = () => <div className=""></div>;

let _unsubscribeGlobalNotify;
const initialNotification = { show: false, proposal: 0, partner_name: '' };

const VerticalLayout = ({ children, searchOptions, setSearchOptions }) => {
    const dispatch = useDispatch();

    const userData = useSelector((state) => state.user.data);

    const { layoutColor, leftSideBarTheme, leftSideBarType, layoutWidth } = useSelector((state) => ({
        layoutColor: state.layout.layoutColor,
        layoutWidth: state.layout.layoutWidth,
        leftSideBarTheme: state.layout.leftSideBarTheme,
        leftSideBarType: state.layout.leftSideBarType,
    }));

    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const [notification, setNotification] = useState(initialNotification);
    
    const onFirebaseSnapshotChange = snapshot => {
        setNotification({
            show: true,
            proposal: snapshot.proposal ?? 0,
        });
    }

    /*
     * layout defaults
     */
    useEffect(() => {
        changeBodyAttribute('data-layout', layoutConstants.LAYOUT_VERTICAL);

        //firebase
        if (_unsubscribeGlobalNotify != null) {
            _unsubscribeGlobalNotify();
        }
        
        const userProfiles = userData?.profiles ?? [];
        if (userData.user_type === 3 && userData.partner_id && (userProfiles.includes('loan_partner_edit') || (userProfiles.includes('loan_partner_write')))) {
            listenerPartnerFirebase('loans_workflow', userData.partner_id.toString(), onFirebaseSnapshotChange).then(unsub => _unsubscribeGlobalNotify = unsub);    
        }
        return () => {
            if (_unsubscribeGlobalNotify != null) {
                _unsubscribeGlobalNotify();
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        changeBodyAttribute('data-layout-color', layoutColor);
    }, [layoutColor]);

    useEffect(() => {
        changeBodyAttribute('data-layout-mode', layoutWidth);
    }, [layoutWidth]);

    useEffect(() => {
        changeBodyAttribute('data-leftbar-theme', leftSideBarTheme);
    }, [leftSideBarTheme]);

    useEffect(() => {
        changeBodyAttribute('data-leftbar-compact-mode', leftSideBarType);
    }, [leftSideBarType]);

    /**
     * Open the menu when having mobile screen
     */
    const openMenu = () => {
        setIsMenuOpened((prevState) => {
            setIsMenuOpened(!prevState);
        });

        if (document.body) {
            if (isMenuOpened) {
                document.body.classList.remove('sidebar-enable');
            } else {
                document.body.classList.add('sidebar-enable');
            }
        }
    };

    const updateDimensions = useCallback(() => {
        // activate the condensed sidebar if smaller devices like ipad or tablet
        if (window.innerWidth <= 760) {
            dispatch(changeSidebarType(layoutConstants.LEFT_SIDEBAR_TYPE_MOBILE_CONDENSED));
        }
        if (window.innerWidth >= 768 && window.innerWidth <= 1028) {
            dispatch(changeSidebarType(layoutConstants.LEFT_SIDEBAR_TYPE_CONDENSED));
        } else if (window.innerWidth > 1028) {
            dispatch(changeSidebarType(layoutConstants.LEFT_SIDEBAR_TYPE_FIXED));
        }
    }, [dispatch]);

    useEffect(() => {
        window.addEventListener('resize', updateDimensions);

        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, [dispatch, updateDimensions]);

    const isMobileCondensed = leftSideBarType === layoutConstants.LEFT_SIDEBAR_TYPE_MOBILE_CONDENSED;
    const isCondensed = leftSideBarType === layoutConstants.LEFT_SIDEBAR_TYPE_CONDENSED;
    const isLight = leftSideBarTheme === layoutConstants.LEFT_SIDEBAR_THEME_LIGHT;

    return (
        <>
            <div className="wrapper">
                <Suspense fallback={loading()}>
                    <LeftSidebar
                        isCondensed={isCondensed}
                        isMobileCondensed={isMobileCondensed}
                        isLight={isLight}
                        hideUserProfile={true}
                    />
                </Suspense>
                <div className="content-page">
                    <div className="content">
                        <Suspense fallback={loading()}>
                            <Topbar
                                openLeftMenuCallBack={openMenu}
                                hideLogo={true}
                                searchOptions={searchOptions}
                                setSearchOptions={setSearchOptions}
                            />
                        </Suspense>
                        <Container style={{ paddingTop: searchOptions.showSearch ? '20px' : 0 }} fluid>
                            <Outlet />
                        </Container>
                        <TempPasswordModal
                            userName={userData.first_name}
                            show={userData.has_temp_password ? window.location.pathname !== '/account/profile' : false}
                        />
                    </div>

                    <Suspense fallback={loading()}>
                        <Footer />
                    </Suspense>
                </div>
                <Notification
                    show={notification.show}  
                    title='Proposta Pendente!'
                    message={`Uma proposta foi colocada em status Pendente ou possui atualizações do analista`}
                    buttonTitle='Ver Proposta'  
                    onUpdateRequested={() => {
                        setNotification({ ...initialNotification, });
                        window.location.href = '/proposals/?proposalId=' + notification.proposal.toString();
                    }}
                    onClose={() => setNotification({ ...initialNotification, })}
                />
            </div>
        </>
    );
};
export default VerticalLayout;
